import {
  autoUpdate,
  FloatingFocusManager,
  FloatingPortal,
  FloatingOverlay,
  useDismiss,
  useInteractions,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from "@floating-ui/react";
import { Icon, InputContainer, InputText } from "@sellar-io/components";
import {
  useEffect,
  useImperativeHandle,
  useId,
  useRef,
  useState,
  type Ref,
} from "react";
import { twMerge } from "tailwind-merge";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import { useMarketSearchSuggestions } from "~/hooks/api/v2/discovery/useMarketSearchSuggestions";
import { useFirebaseUser } from "~/hooks/useFirebaseUser.shared";
import { useRemoteFeatureFlags } from "~/hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "~/hooks/useSellarCurrentBusiness.shared";
import { MarketSearchPanel } from "./MarketSearchPanel";
import { buildMarketSearchGroups } from "./marketSearchGroups";
import {
  marketSearchHistorySchema,
  selectionKey,
  type MarketSearchSelection,
  type SearchOption,
} from "./marketSearchSelections";

export type { MarketSearchSelection } from "./marketSearchSelections";
export type MarketSearchBarProps = {
  onSelect: (selection: MarketSearchSelection) => void;
  postcode?: string;
  inputRef?: Ref<HTMLInputElement>;
  autoFocus?: boolean;
  suggestionsEnabled?: boolean;
  label?: string;
  placeholder?: string;
  fullWidth?: boolean;
  className?: string;
  inputClassName?: string;
  inputContainerClassName?: string;
  panelClassName?: string;
  /** Keep the popup inside an overlay's focus boundary when needed. */
  portalRoot?: HTMLElement | null;
};

export const MarketSearchBar = (props: MarketSearchBarProps) => {
  const { user } = useFirebaseUser();
  const { business } = useSellarCurrentBusiness();
  const historyKey = `marketSearchHistory:${user?.uid ?? "anonymous"}:${business?.id ?? "none"}`;
  return (
    <MarketSearchBarForContext
      key={historyKey}
      {...props}
      historyKey={historyKey}
    />
  );
};

const MarketSearchBarForContext = ({
  onSelect,
  postcode,
  inputRef,
  autoFocus,
  suggestionsEnabled = true,
  label = "Search the market",
  placeholder = "Search products, styles and suppliers",
  fullWidth = false,
  className,
  inputClassName,
  inputContainerClassName,
  panelClassName,
  portalRoot,
  historyKey,
}: MarketSearchBarProps & { historyKey: string }) => {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<"market" | "suppliers">("market");
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);
  const controlRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(inputRef, () => controlRef.current!, []);
  const composing = useRef(false);
  const listId = useId();
  const { business } = useSellarCurrentBusiness();
  const { isActive } = useRemoteFeatureFlags();
  const autocompleteAvailable =
    isActive("marketSearchAutocomplete") && !business?.Supplier?.id;
  const mobile = useMediaQuery("(max-width: 639px)");
  const enabled = open && autocompleteAvailable && suggestionsEnabled;
  const {
    refs: { setReference, setFloating },
    context,
    floatingStyles,
  } = useFloating({
    open: enabled,
    onOpenChange: (nextOpen) => {
      setOpen(nextOpen);
      if (!nextOpen) {
        setHighlightedKey(null);
      }
    },
    strategy: mobile ? "fixed" : "absolute",
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(mobile ? 0 : 16),
      !mobile && flip(),
      shift({ padding: mobile ? 0 : 16 }),
      size({
        padding: mobile ? 0 : 16,
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            height: mobile ? `${availableHeight}px` : "",
          });
        },
      }),
    ],
  });
  const dismiss = useDismiss(context, { escapeKey: false });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);
  const metadata = useMarketSearchSuggestions({
    search: "",
    enabled: enabled && !!business?.Retailer?.id,
  });
  const hasSuppliers = metadata.data?.hasConnectedSuppliers === true;
  const effectiveScope = hasSuppliers ? scope : "market";
  const search = draft.trim();
  const suggestions = useMarketSearchSuggestions({
    search,
    postcode,
    scope: effectiveScope,
    enabled: enabled && (!!search || effectiveScope === "suppliers"),
  });
  const [history, setHistory] = useLocalStorage<MarketSearchSelection[]>(
    historyKey,
    [],
    {
      deserializer: (value) =>
        marketSearchHistorySchema.parse(JSON.parse(value)),
    },
  );
  const { groups, noSupplierMatches } = buildMarketSearchGroups({
    search,
    scope: effectiveScope,
    history,
    data: suggestions.data,
    isLoading: suggestions.isLoading,
    isError: suggestions.isError,
  });
  const options: SearchOption[] = groups.flatMap((group) => group.options);
  const rowKeys = groups.flatMap((group) =>
    group.options.map((option) => `${group.id}:${selectionKey(option)}`),
  );
  const selectedIndex = rowKeys.findIndex((key) => key === highlightedKey);
  const highlight = enabled
    ? selectedIndex >= 0
      ? selectedIndex
      : search && options.length
        ? 0
        : -1
    : -1;
  const setHighlight = (index: number) =>
    setHighlightedKey(rowKeys[index] ?? null);
  const highlighted = enabled ? options[highlight] : undefined;
  const optionId = (index: number) => `${listId}-${index}`;
  useEffect(() => {
    if (highlight >= 0) {
      document
        .getElementById(`${listId}-${highlight}`)
        ?.scrollIntoView({ block: "nearest" });
    }
  }, [highlight, listId]);
  const commit = (selection: SearchOption) => {
    setHistory((previous: MarketSearchSelection[]) =>
      [
        selection,
        ...previous.filter(
          (item) => selectionKey(item) !== selectionKey(selection),
        ),
      ].slice(0, 6),
    );
    setDraft("");
    setOpen(false);
    setHighlight(-1);
    // Dialogs must restore focus outside the input without reopening the keyboard or suggestions.
    containerRef.current?.focus({ preventScroll: true });
    onSelect(selection);
  };
  return (
    <div
      {...getReferenceProps()}
      ref={(element) => {
        containerRef.current = element;
        setReference(element);
      }}
      tabIndex={-1}
      className={twMerge(
        "relative w-full",
        !fullWidth && "max-w-[420px]",
        className,
      )}
      onKeyDown={(event) => {
        if (event.key === "Escape" && enabled) {
          event.preventDefault();
          event.stopPropagation();
          controlRef.current?.focus({ preventScroll: true });
          setOpen(false);
          setHighlight(-1);
        }
      }}
    >
      <InputContainer
        className={twMerge(
          "h-[50px] gap-2 rounded-md px-4 py-2 focus-within:border-[#a34fd2] focus-within:shadow-[2px_2px_16px_0_rgba(0,0,0,0.1),0_0_2px_2px_rgba(163,79,210,0.2)]",
          inputContainerClassName,
        )}
      >
        <Icon icon="MAGNIFY" size="18px" className="text-zinc-800" />
        <InputText
          ref={controlRef}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={enabled}
          aria-controls={enabled ? listId : undefined}
          aria-activedescendant={highlighted ? optionId(highlight) : undefined}
          aria-label={label}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          maxLength={100}
          enterKeyHint="search"
          className={twMerge("text-base leading-5 sm:text-sm", inputClassName)}
          value={draft}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(event) => {
            setDraft(event.target.value);
            setOpen(true);
            setHighlight(-1);
          }}
          onCompositionStart={() => {
            composing.current = true;
          }}
          onCompositionEnd={() => {
            composing.current = false;
          }}
          onKeyDown={(event) => {
            if (composing.current || event.nativeEvent.isComposing) {
              return;
            }
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              if (!autocompleteAvailable) {
                return;
              }
              event.preventDefault();
              setOpen(true);
              const direction = event.key === "ArrowDown" ? 1 : -1;
              let nextHighlight = -1;
              if (options.length) {
                if (highlight < 0) {
                  nextHighlight = direction > 0 ? 0 : options.length - 1;
                } else {
                  nextHighlight =
                    (highlight + direction + options.length) % options.length;
                }
              }
              setHighlight(nextHighlight);
            } else if (event.key === "Escape" && enabled) {
              event.preventDefault();
              event.stopPropagation();
              setOpen(false);
              setHighlight(-1);
            } else if (event.key === "Enter" && (highlighted || search)) {
              event.preventDefault();
              event.stopPropagation();
              commit(
                highlighted ?? {
                  kind: "text",
                  value: search,
                  label: search,
                  scope:
                    noSupplierMatches || suggestions.isError
                      ? "market"
                      : effectiveScope,
                },
              );
            }
          }}
        />
        {mobile && enabled && (
          <button
            type="button"
            aria-label="Close search"
            onPointerDown={(event) => event.preventDefault()}
            onClick={(event) => {
              // Cancel the surrounding label's default input activation.
              event.preventDefault();
              setDraft("");
              setOpen(false);
              setHighlight(-1);
              controlRef.current?.blur();
            }}
            className="flex size-9 shrink-0 items-center justify-center rounded-md text-zinc-500"
          >
            <Icon icon="CLOSE" size="20px" />
          </button>
        )}
      </InputContainer>
      {enabled && (
        <FloatingPortal root={portalRoot}>
          <FloatingOverlay
            lockScroll={mobile}
            className={mobile ? "pointer-events-none z-20" : "contents"}
          >
            <FloatingFocusManager
              context={context}
              modal={false}
              initialFocus={-1}
              returnFocus={false}
            >
              <MarketSearchPanel
                getFloatingProps={getFloatingProps}
                panelRef={setFloating}
                style={floatingStyles}
                listId={listId}
                groups={groups}
                highlight={highlight}
                optionId={optionId}
                onHighlight={setHighlight}
                onSelect={commit}
                scope={effectiveScope}
                hasSuppliers={hasSuppliers}
                onScopeChange={(value) => {
                  setScope(value);
                  setHighlight(-1);
                }}
                className={panelClassName}
                mobile={mobile}
                hasHistory={history.length > 0}
                onClearHistory={() => setHistory([])}
                hasMore={effectiveScope === "suppliers" && suggestions.hasMore}
                loadingMore={suggestions.isLoadingMore}
                onLoadMore={() => suggestions.loadMore()}
                loading={suggestions.isLoading}
                error={suggestions.isError}
                noSupplierMatches={noSupplierMatches}
                search={search}
              />
            </FloatingFocusManager>
          </FloatingOverlay>
        </FloatingPortal>
      )}
    </div>
  );
};
